import {Component, Input, OnDestroy, OnInit, Output, EventEmitter} from '@angular/core';
import {ReportDataCacheService} from '../../services/reportDataCache.service';
import {ReportSelectionService} from '../../services/reportSelection.service';
import {ConfigurationService} from '../../../services/common/configuration/configuration.service';
import {ChartSpinnerHandlerService} from '../../services/chartSpinnerHandler.service';
import {StatisticsApiService} from '../../../services/api/statistics/statistics-api.service';
import {DatePipe} from '@angular/common';
import {DateType} from '../../enums/date-type.enum';
import {ReportFilterType} from '../../enums/report-filter-type.enum';
import {StatisticsElementType} from '../../enums/statistics-element-type.enum';
import * as _ from 'lodash';
import {
  OneTimeConfirmationDialogService
} from '../../../dialogs/one-time-confirmation-dialog/service/one-time-confirmation-dialog.service';


@Component({
  selector: 'graph-filter-panel',
  templateUrl: './graph-filter-panel.component.html',
  styleUrls: ['./graph-filter-panel.component.scss']
})
export class GraphFilterPanelComponent implements OnInit {

  constructor(private reportDataCacheService: ReportDataCacheService,
              private reportSelectionService: ReportSelectionService,
              private configurationService: ConfigurationService,
              private oneTimeConfirmationDialogService: OneTimeConfirmationDialogService,
              private chartSpinnerHandlerService: ChartSpinnerHandlerService,
              private statisticsApiService: StatisticsApiService,
              private datePipe: DatePipe) {
  }

  elementsData: any;
  private selectedItem: any;
  readonly graphFilterStoreKey = 'statistic-range-store-key';


  @Input()
  set externalData(externalData: any) {
    this.elementsData =  externalData;
    if (!!this.elementsData && !!this.selectedItem) {
      this.updateElements(this.selectedItem);
      this.updateQuotaElementsOptions();
    }
  }

  @Output() refreshChart = new EventEmitter();

  @Input()
  set selectedReport(selectedReport: any) {
    if (selectedReport === undefined) {
      return;
    }
    this.selectedItem = selectedReport;
    this.updateFilters(selectedReport);
  }

  datePickerOptions;
  reportIdentifier: string;
  availableFilters: any;
  filterValues: any;
  elementOptions: any;

  ngOnInit() {
    this.datePickerOptions = this.configurationService.getDatePickerOptions();
  }


  updateFilters(selectedReport) {
    this.reportIdentifier = selectedReport.reportIdentifier;
    this.filterValues = this.reportDataCacheService.getReportFilters(selectedReport);
    this.setAvailableFilters(selectedReport);
  }

  onSubmit(identifier, filters) {
    if (this.dateRangeBiggerThanMonth(filters)){
      this.oneTimeConfirmationDialogService.showPopup(this.graphFilterStoreKey,
        'Statistics for the last 31 days of the requested time range will be displayed',
        () => {
          this.reportDataCacheService.setReportFilters(identifier, filters);
          const type = this.reportDataCacheService.getLastReportData(identifier).type;
          this.refreshChart.emit({identifier, type});
        });
    }
    else {
      this.reportDataCacheService.setReportFilters(identifier, filters);
      const type = this.reportDataCacheService.getLastReportData(identifier).type;
      this.refreshChart.emit({identifier, type});
    }
  }

  isRangeSelected() {
    return _.isEqual(this.filterValues.dateType, DateType.RANGE);
  }

  isDateFilterSupported() {
    return this.isSupportedFilter(ReportFilterType.DATE);
  }

  isQuotaStatisticsElementsSupported() {
    return this.isSupportedFilter(ReportFilterType.CALLS_QUOTA_ELEMENTS);
  }

  isSingleStatisticFilterSupported() {
    return this.isSupportedFilter(ReportFilterType.SINGLE_STATISTIC);
  }

  isMultipleStatisticsFilterSupported() {
    return this.isSupportedFilter(ReportFilterType.MULTIPLE_STATISTICS);
  }

  isElementsFilterSupported() {
    return this.isSupportedFilter(ReportFilterType.ELEMENTS);
  }

  isSingleElementFilterSupported() {
    return this.isSupportedFilter(ReportFilterType.SINGLE_ELEMENT);
  }

  isNumberOfElementsFilterSupported() {
    return this.isSupportedFilter(ReportFilterType.NUMBER_OF_ELEMENTS);
  }

  isNumberOfStackedElementsFilterSupported() {
    return this.isSupportedFilter(ReportFilterType.NUMBER_OF_STACKED_ELEMENTS);
  }

  isSubmitButtonEnabled() {
    if (this.chartSpinnerHandlerService.isRunning(this.reportIdentifier)) {
      return false;
    } else if (this.isSupportedFilter(ReportFilterType.ELEMENTS) && this.filterValues.elements.length === 0) {
      return false;
    } else if (this.isSupportedFilter(ReportFilterType.SINGLE_ELEMENT) && _.isNil(this.filterValues.element)) {
      return false;
    } else if (this.isSupportedFilter(ReportFilterType.MULTIPLE_STATISTICS) && this.filterValues.multipleStatistics.length === 0) {
      return false;
    }
    return true;
  }

  onQuotaElementsChange() {
    const elementOptions = (this.filterValues.quotaElementType === 'peerConnection') ?
      this.getElementsByElementType(StatisticsElementType.PEER_CONNECTION, this.selectedItem.reportType) :
      this.getElementsByElementType(StatisticsElementType.RESOURCE_GROUP, this.selectedItem.reportType);

    this.elementOptions = elementOptions.filter(item => ((item.callsQuotaId === this.filterValues.quotaElementId) &&
      !_.isNil(item.callsQuotaId)));
    this.filterValues.elements = this.isNotEmptyArray(this.elementOptions) ? [this.elementOptions[0].id] : [];
  }

  private setReportLastUpdateTime(data) {
    data.lastUpdateTime = this.datePipe.transform(new Date(), 'MMM d, y HH:mm');
  }

  private setAvailableFilters(selection) {
    this.availableFilters = selection.filters;
    this.updateElements(selection);
    this.updateQuotaElementsOptions();
  }

  private updateElements(selection) {
    if (this.isSupportedFilter(ReportFilterType.ELEMENTS) || this.isSupportedFilter(ReportFilterType.SINGLE_ELEMENT)) {
      this.filterValues.elementOptions = this.getElementsByElementType(selection.elementType, selection.reportType);
      if (this.filterValues.elementOptions !== null) { // external data is null
        if (this.isSupportedFilter(ReportFilterType.ELEMENTS)) {
          this.filterValues.elements = this.getSelectMultipleElements();
        } else if (this.isSupportedFilter(ReportFilterType.SINGLE_ELEMENT)) {
          this.filterValues.element = this.getSelectedElement();
        }
      }
    }
    this.elementOptions = this.filterValues.elementOptions;
  }

  private getSelectMultipleElements() {
    if (this.filterValues.elementOptions.length !== 0) {
      // if there is selected elements: get only the available elements, otherwise select the first element as default
      return this.filterValues.elements && this.filterValues.elements.length > 0 ?
        this.filterAvailableElements(this.filterValues.elements, this.filterValues.elementOptions) :
        [this.filterValues.elementOptions[0].id];
    }

    return [];
  }

  private getSelectedElement() {
    if (!_.isNil(this.filterValues.element)) {
      return this.filterValues.element;
    }

    return (this.filterValues.elementOptions.length !== 0) ?
      this.filterValues.elementOptions[0].id : null;
  }

  private updateQuotaElementsOptions() {
    if (_.isNil(this.elementsData) || !this.isQuotaStatisticsElementsSupported()) {
      return;
    }

    if (this.callsQuotaFilterNotInitialize()) {
      this.setQuotaElementsOptions();
      this.setQuotaTypesOptions();
    }

    this.onQuotaElementsChange();
  }

  private isSupportedFilter(filter) {
    if (this.availableFilters) {
      return this.availableFilters.indexOf(filter) !== -1;
    }
    return false;
  }

  private filterAvailableElements(selectedElements, allElements) {
    const allElementsIds = [];
    allElements.forEach((element) => {
      allElementsIds.push(element.id);
    });

    return selectedElements.filter((element) => {
      return allElementsIds.indexOf(element) !== -1;
    });
  }

  private getElementsByElementType(elementType, reportType) {
    if (elementType !== StatisticsElementType.ARM) {
      return this.getElements(elementType, reportType);
    }
    return [];
  }

  private getElements(elementType, reportType) {
    if (this.elementsData === undefined) {
      return null;
    }
    switch (elementType) {
      case StatisticsElementType.NODE:
        return this.elementsData.nodes;
      case StatisticsElementType.ROUTER:
        return this.elementsData.routers;
      case StatisticsElementType.PEER_CONNECTION:
        return this.elementsData.pCons;
      case StatisticsElementType.CONNECTION:
        return this.elementsData.connections;
      case StatisticsElementType.ROUTING_GROUP:
        return this.elementsData.routingGroups;
      case StatisticsElementType.ROUTING_RULE:
        return this.elementsData.routingRules;
      case StatisticsElementType.RESOURCE_GROUP:
        return this.filterResourceGroup(reportType);
      case StatisticsElementType.CALLS_QUOTA:
        return this.elementsData.callsQuotas;
      case StatisticsElementType.CUSTOMER:
        return this.elementsData.customers;
      case StatisticsElementType.VOIP_PEER:
        return this.elementsData.voipPeers;
    }
  }

  private filterResourceGroup(reportType) {
    if (reportType === StatisticsElementType.CALLS_QUOTA) {
      return this.elementsData.resourceGroups.filter(resourceGroup => resourceGroup.type === 'PEER_CONNECTION');
    }

    return this.elementsData.resourceGroup;
  }

  private callsQuotaFilterNotInitialize() {
    return _.isNil(this.filterValues.quotaElementsOptions) ||
      _.isNil(this.filterValues.quotaTypesOptions) ||
      _.isNil(this.filterValues.quotaElementType);
  }

  private isNotEmptyArray(listItem): boolean {
    return _.isArray(listItem) && listItem.length > 0;
  }

  private setQuotaTypesOptions() {
    this.filterValues.quotaTypesOptions = [{
      id: 'peerConnection',
      name: 'Peer Connection'
    }, {
      id: 'resourceGroup',
      name: 'Resource Group'
    }];

    this.filterValues.quotaElementType = 'peerConnection';
  }

  private setQuotaElementsOptions() {
    this.filterValues.quotaElementsOptions = this.getElements(StatisticsElementType.CALLS_QUOTA, this.selectedItem.reportType);
    if (this.isNotEmptyArray(this.filterValues.quotaElementsOptions)) {
      this.filterValues.quotaElementId = this.filterValues.quotaElementsOptions[0].id;
    }
  }

  private dateRangeBiggerThanMonth(filter): boolean {
      if (filter.dateType === 'RANGE'){
        return Math.round((filter.date.endDate.toDate().getTime() - filter.date.startDate.toDate().getTime()) / (1000 * 3600 * 24 )) > 31;
      }
      else{
        switch (filter.relativeTimeUnit){
          case 'MONTH':
            if (filter.relativeTimeUnitValue > 1){
              return true;
            }
            break;
          case 'WEEK':
            if (filter.relativeTimeUnitValue > 4){
              return true;
            }
            break;
          case 'DAY':
            if (filter.relativeTimeUnitValue > 31){
              return true;
            }
            break;
          default:
            return false;
        }
      }
  }

 // onDatePick($event: any): void {
  //  this.filterValues.date = $event
  //  this.search.startDate = $event.startDate.toISOString();
  //  this.search.endDate = $event.endDate.toISOString();
 // }




}
